import AadeCaseSearchOrchestrator from "src/aadeCase/components/AadeCaseSearchOrchestrator";
import { useTranslation } from "react-i18next";
import useGetAdminBasicFilters from "src/aadeCase/hooks/useGetAdminBasicFilters";
import React, { useEffect, useMemo, useState } from "react";

import useGetAdminActiveAadeCases from "src/aadeCase/data-access/useGetAdminActiveAadeCases";
import { TCaseFilter } from "src/aadeCase/data-access/types";
import { FieldValues } from "react-hook-form";
import useCustomMaterialTable from "src/shared/hooks/useCustomMaterialTable";
import getTableLang from "src/shared/services/languages/MaterialReactTable/getTableLang";
import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { format, parseISO } from "date-fns";
import { Link } from "react-router-dom";
import i18n from "src/shared/services/languages/i18n";
import useGetChamberUsers from "src/aadeCase/data-access/useGetChamberUsers";
import { useAuth } from "src/shared/context/useAuth";

export default function AdminActiveAadeCases() {
  const { t } = useTranslation();
  const { isSystemAdmin } = useAuth();
  const filters: TCaseFilter[] = useGetAdminBasicFilters();
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageSize: 10,
    pageIndex: 0,
  });
  const [enableSearch, setEnableSearch] = useState<Boolean>(true);
  const [searchFilters, setSearchFilters] = useState({});

  const {
    data: adminActiveAadeCasesResponse,
    isLoading: isLoadingAdminActiveAadeCases,
  } = useGetAdminActiveAadeCases({
    searchFilters,
    enableSearch,
    setEnableSearch,
    paginationState,
  });
  const { data: chamberUserResponse, isLoading: isLoadingChamberUsers } =
    useGetChamberUsers(isSystemAdmin);

  const adminActiveAadeCaseFilters: TCaseFilter[] = [
    ...filters,
    {
      label: t("Αρμόδιος Χρήστης"),
      component: {
        type: "autoComplete" as const,
        options: chamberUserResponse?.data?.chamberUsers,
        loading: isLoadingChamberUsers,
      },
      name: "chamberUser",
    },
  ];

  const handleSearch = (data: FieldValues) => {
    setSearchFilters({
      ...data,
      date_from: data.date_from ? format(data.date_from, "yyyy-MM-dd") : null,
      date_to: data.date_to ? format(data.date_to, "yyyy-MM-dd") : null,
      legalType: data.legalType ? data.legalType.value : null,
      chamber: data.chamber ? data.chamber.value : null,
      chamberUser: data.chamberUser ? data.chamberUser.value : null,
    });
    setEnableSearch(true);
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: t("Αριθμός υπόθεσης"),
        accessorKey: "case_id",
        accessorFn: (originalRow) => {
          return (
            <Link
              to={`${"/admin/manageAadeCaseApplication"}/${originalRow.case_id}`}
            >
              {originalRow.case_id}
            </Link>
          );
        },
      },
      {
        header: t("Αριθμός Γ.Ε.ΜΗ."),
        accessorKey: "company_id",
      },
      {
        header: t("ΑΦΜ"),
        accessorKey: "afm",
      },
      {
        header: t("Νομική Μορφή"),
        accessorKey: "legalType",
      },
      {
        header: t("Πλήθος αναντιστοιχιών"),
        accessorKey: "subcase_count",
      },
      {
        header: t("Αρμόδιος Χρήστης"),
        accessorKey: "responsible_user",
      },
      {
        header: t("Ημερομηνία Δημιουργίας"),
        accessorKey: "date_created",
        accessorFn: (originalRow) => {
          return (
            <>
              {format(
                parseISO(originalRow.date_created),
                "dd/MM/yyyy HH:mm:ss",
              )}
            </>
          );
        },
      },
      {
        header: t("Επιμελητήριο"),
        accessorKey: "chamber",
      },
    ],
    [i18n.language],
  );

  const table = useCustomMaterialTable({
    columns,
    data: adminActiveAadeCasesResponse?.data.rows ?? [],
    onPaginationChange: (updater) => {
      setPaginationState((previous) => updater(previous));
      setEnableSearch(true);
    },
    localization: getTableLang(),
    rowCount: adminActiveAadeCasesResponse?.data.rowCount ?? 0,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableSorting: false,
    state: {
      isLoading: isLoadingAdminActiveAadeCases,
      showProgressBars: isLoadingAdminActiveAadeCases,
      pagination: paginationState,
    },
    manualPagination: true,
  });

  return (
    <AadeCaseSearchOrchestrator
      title={t("Εκκρεμείς Υποθέσεις Εκκαθάρισης")}
      subtitle={t(
        "Στην οθόνη αυτή εμφανίζονται υποθέσεις που έχουν ανατεθεί σε υπάλληλο της Υ. Γ.Ε.ΜΗ. και δεν έχουν ακόμη διεκπεραιωθεί.",
      )}
      filters={adminActiveAadeCaseFilters}
      handleSearch={handleSearch}
      table={table}
    />
  );
}
